import { create } from 'zustand';
import type { User } from '@responsedock/types';
import type { EmbedTheme } from '@responsedock/types';
import type { EmbedCapability, EmbedSubjectContext, EmbedTenantContext, EmbedUserContext } from '@responsedock/types';

export type EmbeddedTheme = EmbedTheme;

export interface EmbeddedContext {
  workspaceId: string;
  role: 'admin' | 'respondent';
  mode: 'workspace' | 'dashboard' | 'setup' | 'builder' | 'results' | 'respondent' | 'public-results';
  surveyId?: string | null;
  allowedSurveyIds?: string[];
  externalUserId?: string | null;
  allowedOrigin?: string | null;
  returnUrl?: string | null;
  capabilities?: EmbedCapability[];
  tenant?: EmbedTenantContext | null;
  hostUser?: EmbedUserContext | null;
  subject?: EmbedSubjectContext | null;
  theme: EmbeddedTheme;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isEmbedded: boolean;
  embedBearerToken: string | null;
  embedContext: EmbeddedContext | null;
  embedError: string | null;
  useMockData: boolean;
  setUser: (user: User | null) => void;
  setLoading: (v: boolean) => void;
  setIsEmbedded: (v: boolean) => void;
  setEmbedBearerToken: (token: string | null) => void;
  setEmbedContext: (context: EmbeddedContext | null) => void;
  setEmbedTheme: (theme: EmbeddedTheme) => void;
  setEmbedError: (error: string | null) => void;
  setUseMockData: (v: boolean) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  isEmbedded: false,
  embedBearerToken: null,
  embedContext: null,
  embedError: null,
  useMockData: false,
  setUser: (user) => set({ user }),
  setLoading: (v) => set({ isLoading: v }),
  setIsEmbedded: (v) => set({ isEmbedded: v }),
  setEmbedBearerToken: (token) => set({ embedBearerToken: token }),
  setEmbedContext: (context) => set({ embedContext: context }),
  // Hosts may resend the same theme (for example on every ready handshake).
  // Keep the context identity unchanged then, so the app theme and embed bridge do not re-run.
  setEmbedTheme: (theme) => set((state) => (
    !state.embedContext || JSON.stringify(state.embedContext.theme) === JSON.stringify(theme)
      ? state
      : { embedContext: { ...state.embedContext, theme } }
  )),
  setEmbedError: (error) => set({ embedError: error }),
  setUseMockData: (v) => set({ useMockData: v }),
}));
